import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';
import {
	StripeAddressElementChangeEvent,
	StripeError,
	StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import debounce from 'lodash/debounce';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';

import { calculatingSelectedContractedLives } from '../../../utils';
import messages from './messages';
import { Container, Header, StyledHr } from './styles';

interface FormState {
	payment: boolean;
	address: boolean;
}

type StripeElementType = 'payment' | 'address';

async function getTaxValueCalculations(calculateTaxValues: () => Promise<void>): Promise<void> {
	if (calculateTaxValues) await calculateTaxValues();
}

const debounceBillingDetails = debounce(getTaxValueCalculations, 3000);

const StripePaymentElementForm: React.FC<{
	fullName?: string;
	calculateTaxValues?: () => Promise<void>;
	setIsLoadingTaxes?: (isLoading: boolean) => void;
	setShouldShowTaxAmount?: (shouldShowTaxAmount: boolean) => void;
	context?: {
		location: string;
		variant: string;
		companySize: string;
		selectedCoveredLives: number;
	};
}> = ({ calculateTaxValues, setIsLoadingTaxes, setShouldShowTaxAmount, context }) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const [formState, setFormState] = useState<FormState>({ payment: false, address: false });

	const onLoadError = (
		event: { elementType: 'payment' | 'address'; error: StripeError },
		elementType: string,
	): void => {
		logEvent(`Teams : Checkout : ${elementType} : Stripe: Load Error`, {
			error: event.error.message ?? '',
			selectedCoveredLives: context?.selectedCoveredLives ?? '',
			selectedContractedLives: calculatingSelectedContractedLives(context?.selectedCoveredLives ?? 0),
			companySize: context?.companySize ?? '',
			location: context?.location ?? '',
			variant: context?.variant ?? '',
		});
	};

	const handleChange = async (
		event: (StripePaymentElementChangeEvent | StripeAddressElementChangeEvent) & {
			error?: StripeError;
		},
		elementType: StripeElementType,
	): Promise<void> => {
		if (setShouldShowTaxAmount) setShouldShowTaxAmount(true);
		if (setIsLoadingTaxes) setIsLoadingTaxes(true);
		const { complete } = event;
		setFormState(prev => ({ ...prev, [elementType]: complete }));
		const state = { ...formState, [elementType]: complete };
		const isFormValid = Object.values(state).every(value => value);

		if (isFormValid && calculateTaxValues) debounceBillingDetails(calculateTaxValues)?.catch(() => {});
		else if (setShouldShowTaxAmount) setShouldShowTaxAmount(false);
	};

	return (
		<Container data-testid="stripe-payment-element-form">
			<Header>{formatMessage(messages.headerWithOptions)}</Header>
			<div data-testid="payment-element">
				<PaymentElement
					options={{
						wallets: { applePay: 'never', googlePay: 'never' },
						terms: { card: 'never' },
						layout: 'tabs',
					}}
					onLoadError={event => onLoadError(event, 'Payment')}
					onChange={e => handleChange(e, 'payment')}
				/>
			</div>
			<StyledHr />
			<Header>{formatMessage(messages.billingHeader)}</Header>
			<div data-testid="address-element">
				<AddressElement
					options={{
						mode: 'billing',
					}}
					onLoadError={event => onLoadError(event, 'Address')}
					onChange={e => handleChange(e, 'address')}
				/>
			</div>
		</Container>
	);
};

export default StripePaymentElementForm;
